
import { useParams } from "react-router-dom";
import { usePublicLegalDocument } from "@/hooks/useLegalDocuments";
import { useSEO } from "@/hooks/useSEO";
import Header from "@/components/header/ModernHeader";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import BackButton from "@/components/navigation/BackButton";

interface LegalDocumentPageProps {
  document?: any;
  isLoading?: boolean;
  error?: Error | null;
  defaultTitle?: string;
  defaultContent?: string;
}

const LegalDocumentPage = ({ 
  document: propDocument, 
  isLoading: propIsLoading, 
  error: propError, 
  defaultTitle, 
  defaultContent 
}: LegalDocumentPageProps = {}) => {
  const { type } = useParams<{ type: string }>();
  
  // Use props if provided, otherwise fetch from URL params
  const { data: fetchedDocument, isLoading: fetchIsLoading, error: fetchError } = usePublicLegalDocument(
    type || '', 
    { enabled: !propDocument && !!type }
  );

  // Determine which values to use
  const document = propDocument || fetchedDocument;
  const isLoading = propIsLoading !== undefined ? propIsLoading : fetchIsLoading;
  const error = propError !== undefined ? propError : fetchError;

  // SEO setup for legal document page
  useSEO({
    pageType: 'page',
    fallbackTitle: document ? `${document.title} | VRAM Store` : (defaultTitle ? `${defaultTitle} | VRAM Store` : 'Legal Document | VRAM Store'),
    fallbackDescription: document ? 
      `Read our ${document.document_type} policy. Last updated: ${document.effective_date || 'Recently'}.` :
      'Legal document and policies for VRAM Store.',
    customKeywords: `${document?.document_type || 'legal'}, policy, terms, privacy, VRAM Store`
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Card>
            <CardHeader>
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Card>
            <CardContent className="pt-6">
              {defaultTitle && defaultContent ? (
                <div>
                  <h1 className="text-3xl font-bold mb-6">{defaultTitle}</h1>
                  <p className="text-muted-foreground">{defaultContent}</p>
                </div>
              ) : (
                <div className="flex items-center justify-center text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mr-3" />
                  <p>Document not found or unavailable.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <BackButton to="/" label="Back to Home" />
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">{document.title}</CardTitle>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>Document Type: {document.document_type}</p>
              <p>Version: {document.version}</p>
              {document.effective_date && (
                <p>Effective Date: {new Date(document.effective_date).toLocaleDateString()}</p>
              )}
              {document.last_reviewed_at && (
                <p>Last Reviewed: {new Date(document.last_reviewed_at).toLocaleDateString()}</p>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {document.content ? (
              <div 
                className="prose max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: document.content }}
              />
            ) : (
              <p className="text-muted-foreground">Document content is not available.</p>
            )}
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default LegalDocumentPage;
